// BizTracker Ishida Upgrade & Replacement Center.
//
// Specification §31 and CLAUDE-ISHIDA-START-HERE agree on the rule that shapes
// this whole module: never claim a direct replacement without evidence. Today
// every legacy record BizTracker holds is `needs_review` with a null
// replacement, so `needs_review` is not an error path here — it is the normal
// and correct answer, and it has to be a decent customer experience rather than
// a dead end.
//
// MATCHING IS EXACT, DELIBERATELY. Fuzzy matching an old model number is how a
// customer with an AC-3000 gets told about an AC-300 and buys the wrong
// migration. Only an exact normalised match counts; everything else routes to a
// specialist who can look at the serial plate.
//
// This module makes no network call. PR 4 owns the lead form and HubSpot.

export const REPLACEMENT_TYPES = Object.freeze([
  'direct replacement',
  'functional replacement',
  'recommended upgrade',
  'repair instead'
]);

// The "I do not know my model" path. §12 makes this both identification and
// lead capture; the fields are declared here so PR 4 can wire them without
// re-deciding what to collect.
export const IDENTIFY_FIELDS = Object.freeze([
  { id: 'scale_photo', label: 'Photo of the scale', type: 'file', accept: 'image/*' },
  { id: 'serial_plate_photo', label: 'Photo of the model or serial plate', type: 'file', accept: 'image/*' },
  { id: 'label_photo', label: 'Photo of a label it prints today', type: 'file', accept: 'image/*' },
  { id: 'current_pos', label: 'What POS system do you run?', type: 'text' },
  { id: 'use_case', label: 'What do you weigh and label?', type: 'textarea' }
]);

// B4 — where a replacement enquiry actually goes.
//
// The `ishida-replacement-help` and `ishida-quote` pages were never created and will not
// be; both enquiries convert on the Contact page that already exists. The two topics stay
// apart because they are different conversations: an upgrade has a confirmed target model
// to quote, a review has none yet and is asking us to identify the scale from a photo.
//
// Those paths are described rather than spelled, here and below. The link checker reads
// every line of a scanned file, comments included — correctly, since a comment is where a
// stale link hides — so a comment quoting a dead path reports as a dead link.
//
// See the matching constant in ishida-model-finder.js for why a caller appends `&amp;`
// rather than a bare `&`.
export const ISHIDA_UPGRADE_CONTACT = '/pages/contact?topic=ishida-upgrade';
export const ISHIDA_REPLACEMENT_REVIEW_CONTACT = '/pages/contact?topic=ishida-replacement-review';

// Everything that is not a letter or a digit is noise: "AC-300", "ac 300",
// "A.C./300" and "AC300" are one model written four ways.
export function normalizeModelQuery(value) {
  if (typeof value === 'number') value = String(value);
  if (typeof value !== 'string') return '';
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

// A replacement claim is only as good as the evidence behind it. A `direct
// replacement` needs an explicit source; the softer types need a stated
// confidence. Anything else collapses back to `needs_review` rather than
// reaching a customer as fact.
function evidencedClaim(record) {
  if (!record) return null;
  const type = record.replacement_type;
  const target = record.current_replacement;

  if (!type || !target) return null;
  if (!REPLACEMENT_TYPES.includes(type)) return null;
  if (type === 'direct replacement' && !record.evidence_url) return null;

  return {
    current_replacement: target,
    replacement_type: type,
    replacement_confidence: record.replacement_confidence ?? null,
    evidence_url: record.evidence_url ?? null
  };
}

export function findReplacement(query, records) {
  const wanted = normalizeModelQuery(query);
  const list = Array.isArray(records) ? records : [];

  const record = wanted
    ? list.find(r => normalizeModelQuery(r?.old_model) === wanted)
    : undefined;

  if (!record) {
    return {
      found: false,
      query: typeof query === 'string' ? query : '',
      old_model: null,
      status: 'needs_review',
      current_replacement: null,
      replacement_type: null,
      replacement_confidence: null,
      evidence_url: null,
      source: null,
      cta: 'request_review'
    };
  }

  const claim = evidencedClaim(record);

  return {
    found: true,
    query: typeof query === 'string' ? query : '',
    old_model: record.old_model,
    family: record.family ?? null,
    status: claim ? (record.status ?? 'verified_legacy') : 'needs_review',
    current_replacement: claim?.current_replacement ?? null,
    replacement_type: claim?.replacement_type ?? null,
    replacement_confidence: claim?.replacement_confidence ?? null,
    evidence_url: claim?.evidence_url ?? null,
    source: record.source ?? null,
    supplier_status: record.supplier_status ?? null,
    supplier_status_observed_at: record.supplier_status_observed_at ?? null,
    cta: claim ? 'plan_my_upgrade' : 'request_review'
  };
}

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ------------------------------------------------- current models and part numbers
//
// A customer may type the model or the Rice Lake part number of a scale Rice Lake sells TODAY (for example the Uni-5 Bench, parts 119356,
// 154737 and 155264). Recognising it says exactly one thing: this is a current model that Rice Lake lists. It does not say the model replaces
// anything, and it is looked up only AFTER the legacy records, so every legacy hold and lifecycle restriction (a discontinued model, a
// needs-review record) is untouched. Only finder-eligible models are recognised, so a discontinued Uni-9H or a research stub is not.
export const ISHIDA_QUOTE_CONTACT = '/pages/contact?topic=ishida-quote';

export function findCurrentModel(query, models) {
  const wanted = normalizeModelQuery(query);
  if (!wanted) return null;
  const list = (Array.isArray(models) ? models : []).filter(m => m && m.finder_eligible === true);

  for (const m of list) {
    const cfg = (m.configurations || []).find(c => normalizeModelQuery(c && c.supplier_part_number) === wanted);
    if (cfg) return { kind: 'part', model: m.model, configuration: cfg.configuration, part_number: cfg.supplier_part_number };
    const pn = (m.supplier_part_numbers || []).find(p => normalizeModelQuery(p) === wanted);
    if (pn) return { kind: 'part', model: m.model, configuration: null, part_number: pn };
  }
  const byName = list.find(m => normalizeModelQuery(m.model) === wanted);
  if (byName) {
    return { kind: 'model', model: byName.model, configurations: (byName.configurations || []).map(c => ({ configuration: c.configuration, part_number: c.supplier_part_number })) };
  }
  const family = list.filter(m => m.family && normalizeModelQuery(m.family) === wanted);
  if (family.length) {
    return {
      kind: 'family',
      family: family[0].family,
      models: family.map(m => ({
        model: m.model,
        configurations: (m.configurations || []).map(c => ({ configuration: c.configuration, part_number: c.supplier_part_number })),
        part_numbers: m.supplier_part_numbers || []
      }))
    };
  }
  return null;
}

// Customer-facing capitalization (Ishida styles the family UNI-7); identifiers in links keep the catalog spelling.
function displayModelName(n) {
  return String(n == null ? '' : n).replace(/^Uni-/, 'UNI-');
}

function quoteLink(label) {
  return ISHIDA_QUOTE_CONTACT + '&amp;model=' + encodeURIComponent(label);
}

function renderCurrentModel(c) {
  const parts = ['<div class="ishida-replacement__result ishida-replacement__result--current">'];
  // The review page shows what was looked up, so the link carries it (the exact identifier, never a display spelling).
  const reviewed = c.kind === 'family' ? c.family : c.model;
  const reviewCta = '<a class="ishida-replacement__cta ishida-replacement__cta--secondary" href="' + ISHIDA_REPLACEMENT_REVIEW_CONTACT +
    (reviewed ? '&amp;model=' + encodeURIComponent(reviewed) : '') +
    '">Ask a specialist to review my scale</a>';
  const noReplacement = '<p class="ishida-replacement__note-line">This is a current model, not a legacy one, so there is no replacement to show. ' +
    'If you are replacing an older scale, a specialist confirms what carries over — labels, PLUs, interfaces — before recommending anything.</p>';

  if (c.kind === 'family') {
    parts.push('<h3>' + escapeHtml(c.family) + '</h3>');
    parts.push('<p class="ishida-replacement__status">Status: <strong>current family</strong> — we currently list these ' + escapeHtml(c.family) +
      ' configurations:</p>');
    parts.push('<ul class="ishida-replacement__configs">' + c.models.map(m => {
      const rows = m.configurations.length
        ? m.configurations.map(cfg => '<li>' + escapeHtml(displayModelName(m.model)) + ': ' + escapeHtml(cfg.configuration) +
            (cfg.part_number ? ' <span class="ishida-model__pn">Part ' + escapeHtml(cfg.part_number) + '</span>' : '') + '</li>').join('')
        : '<li>' + escapeHtml(displayModelName(m.model)) + (m.part_numbers.length ? ' <span class="ishida-model__pn">Part ' + escapeHtml(m.part_numbers.join(', ')) + '</span>' : '') + '</li>';
      return rows;
    }).join('') + '</ul>');
    parts.push('<p>Other configurations of the family are not covered here yet. Tell us which one you have and a specialist will identify it.</p>');
    parts.push(noReplacement);
    parts.push(reviewCta);
  } else {
    const label = c.kind === 'part' && c.configuration ? c.model + (c.configuration.startsWith('Bench') && c.model.endsWith(' Bench') ? c.configuration.slice('Bench'.length) : ' ' + c.configuration) : c.model;
    parts.push('<h3>' + escapeHtml(displayModelName(c.model)) + (c.kind === 'part' ? ' <span class="ishida-model__pn">Part ' + escapeHtml(c.part_number) + '</span>' : '') + '</h3>');
    parts.push('<p class="ishida-replacement__status">Status: <strong>current model</strong> — this ' +
      (c.kind === 'part' ? 'part number' : 'model') + ' is part of the current Ishida line.</p>');
    if (c.kind === 'part' && c.configuration) parts.push('<p>' + escapeHtml(c.configuration) + '</p>');
    if (c.kind === 'model' && c.configurations.length) {
      parts.push('<ul class="ishida-replacement__configs">' + c.configurations.map(cfg => '<li>' + escapeHtml(cfg.configuration) +
        (cfg.part_number ? ' <span class="ishida-model__pn">Part ' + escapeHtml(cfg.part_number) + '</span>' : '') + '</li>').join('') + '</ul>');
    }
    parts.push(noReplacement);
    parts.push('<a class="ishida-replacement__cta" href="' + quoteLink(label) + '">Request a quote for this model</a>');
    parts.push(reviewCta);
  }
  parts.push('</div>');
  return parts.join('');
}

export function renderReplacementResult(result) {
  if (!result) return '';
  if (result.current) return renderCurrentModel(result.current);
  const parts = ['<div class="ishida-replacement__result">'];

  if (!result.found) {
    parts.push('<h3>We do not have a record for that model yet</h3>');
    parts.push('<p>We will not guess at a replacement — the wrong migration costs more than the scale. ' +
      'Request replacement assistance. Include the model number and serial number if available, and an Ishida ' +
      'specialist will identify it for you.</p>');
    parts.push('<a class="ishida-replacement__cta" href="' + ISHIDA_REPLACEMENT_REVIEW_CONTACT +
      (result.query ? '&amp;model=' + encodeURIComponent(String(result.query).slice(0, 80)) : '') +
      '">Talk With an Ishida Specialist</a>');
    parts.push('</div>');
    return parts.join('');
  }

  parts.push('<h3>' + escapeHtml(displayModelName(result.old_model)) + '</h3>');

  // What the supplier says about the model itself. Only the one value we have evidence for is
  // rendered as a claim; anything else is ignored rather than echoed. It never implies a replacement.
  if (result.supplier_status === 'discontinued') {
    parts.push('<p class="ishida-replacement__supplier-status">This model is listed as discontinued' +
      (result.supplier_status_observed_at ? ' (checked ' + escapeHtml(result.supplier_status_observed_at) + ')' : '') + '.</p>');
  }

  if (result.current_replacement) {
    parts.push('<p class="ishida-replacement__type">Relationship: <strong>' +
      escapeHtml(result.replacement_type) + '</strong>' +
      (result.replacement_confidence
        ? ' (confidence: ' + escapeHtml(result.replacement_confidence) + ')'
        : '') + '</p>');
    parts.push('<p class="ishida-replacement__target">Current solution: <strong>' +
      escapeHtml(result.current_replacement) + '</strong></p>');
    if (result.evidence_url) {
      parts.push('<p class="ishida-replacement__evidence">More information: <a href="' +
        escapeHtml(result.evidence_url) + '" rel="nofollow noopener" target="_blank">' +
        escapeHtml(result.evidence_url) + '</a></p>');
    }
    parts.push('<a class="ishida-replacement__cta" href="' + ISHIDA_UPGRADE_CONTACT + '&amp;replacing=' +
      encodeURIComponent(result.old_model) + '">Plan My Upgrade</a>');
  } else {
    parts.push('<p class="ishida-replacement__status">Status: <strong>needs review</strong> — ' +
      'we hold information on this model but have <em>not yet confirmed</em> a current replacement.</p>');
    parts.push('<p>We only recommend a replacement when it is documented. ' +
      'An Ishida specialist will confirm what carries over — labels, PLUs, interfaces — before recommending anything.</p>');
    parts.push('<a class="ishida-replacement__cta" href="' + ISHIDA_REPLACEMENT_REVIEW_CONTACT + '&amp;model=' +
      encodeURIComponent(result.old_model) + '">Request a Replacement Review</a>');
  }

  parts.push('</div>');
  return parts.join('');
}

// ---------------------------------------------------------------- DOM wiring

export function mountReplacementCenter(root, catalog) {
  if (!root || !catalog) return;
  const form = root.querySelector('[data-ishida-replacement-form]');
  const input = root.querySelector('[data-ishida-replacement-input]');
  const results = root.querySelector('[data-ishida-replacement-results]');
  if (!form || !input || !results) return;

  form.addEventListener('submit', event => {
    event.preventDefault();
    // Legacy records first, so every legacy hold stays exactly as it was; a current model or part number is recognised only when no
    // legacy record matches.
    const legacy = findReplacement(input.value, catalog.legacy);
    const current = legacy.found ? null : findCurrentModel(input.value, catalog.models);
    results.innerHTML = renderReplacementResult(current ? { current } : legacy);
  });
}

if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    for (const root of document.querySelectorAll('[data-ishida-replacement]')) {
      const src = root.getAttribute('data-catalog-url');
      if (!src) continue;
      fetch(src)
        .then(r => r.json())
        .then(catalog => mountReplacementCenter(root, catalog))
        .catch(() => {
          const results = root.querySelector('[data-ishida-replacement-results]');
          if (results) {
            results.innerHTML =
              '<p>The replacement lookup is unavailable right now. ' +
              '<a href="' + ISHIDA_REPLACEMENT_REVIEW_CONTACT +
              '">Talk with an Ishida specialist</a>.</p>';
          }
        });
    }
  });
}
